import { IconButton } from 'Components/shared/IconButton';
import { ListPopover } from 'Components/shared/ListPopover';
import { MobXProviderContext, observer } from 'mobx-react';
import { ContactModelBase } from 'Models/ContactModel';
import { ContactModelV2 } from 'Models/ContactModelV2';
import React, { useMemo } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { ContactDetailsActionsProps } from './index.types';
import { getPhoneOptions } from './utils/getPhoneOptions';
import { useContactActions } from './utils/useContactActions';

export const ContactDetailsActions = observer(
  ({ contact }: ContactDetailsActionsProps) => {
    const { person: personStore } =
      React.useContext<RootStoreProps>(MobXProviderContext);

    const {
      callPerson,
      callExternal,
      messagePerson,
      messageExternal,
      videoCallPerson,
    } = useContactActions();

    const isInternal =
      contact instanceof ContactModelV2 && contact.source === 'Bhive';
    const isUnsavedContact = contact instanceof ContactModelBase;

    const chatButton = useMemo(() => {
      if (
        isInternal ||
        isUnsavedContact ||
        contact.phoneNumbers?.length === 1
      ) {
        return (
          <IconButton
            icon={'chat'}
            onClick={() =>
              isInternal
                ? messagePerson(contact.sourceId)
                : isUnsavedContact
                ? messageExternal(contact.phoneNumber)
                : messageExternal(contact.phoneNumbers[0].number)
            }
            size={'medium'}
            testid={'contact-details-chat'}
            variant="secondary"
            label={isInternal ? 'Chat' : 'SMS'}
          />
        );
      }

      const phoneNumbers = getPhoneOptions(
        contact.phoneNumbers,
        (phoneNumber) => messageExternal(phoneNumber)
      );

      return (
        <ListPopover side={'bottom'} options={phoneNumbers}>
          <IconButton
            icon={'chat'}
            onClick={() => {}}
            size={'medium'}
            testid={'contact-details-chat'}
            variant="secondary"
            label={isInternal ? 'Chat' : 'SMS'}
          />
        </ListPopover>
      );
    }, [contact, isInternal, isUnsavedContact, messageExternal, messagePerson]);

    const callButton = useMemo(() => {
      if (
        isInternal ||
        isUnsavedContact ||
        contact.phoneNumbers?.length === 1
      ) {
        return (
          <IconButton
            icon={'call'}
            onClick={() => {
              isInternal
                ? callPerson(contact.sourceId)
                : isUnsavedContact
                ? callExternal(contact.phoneNumber)
                : callExternal(contact.phoneNumbers[0].number);
            }}
            size={'medium'}
            testid={'contact-details-call'}
            variant="secondary"
            label="Call"
          />
        );
      }

      const phoneNumbers = getPhoneOptions(
        contact.phoneNumbers,
        (phoneNumber) => callExternal(phoneNumber)
      );

      return (
        <ListPopover side={'bottom'} options={phoneNumbers}>
          <IconButton
            icon={'call'}
            onClick={() => {}}
            size={'medium'}
            testid={'contact-details-call'}
            variant="secondary"
            label="Call"
          />
        </ListPopover>
      );
    }, [callExternal, callPerson, contact, isInternal, isUnsavedContact]);

    const videoButton = useMemo(() => {
      // Not internal contact
      if (contact instanceof ContactModelBase || contact.source !== 'Bhive') {
        return null;
      }

      return (
        <IconButton
          icon={'video'}
          onClick={() => {
            videoCallPerson(contact.sourceId);
          }}
          size={'medium'}
          testid={'contact-details-video'}
          variant="secondary"
          label="Video"
        />
      );
    }, [contact, videoCallPerson]);

    if (contact instanceof ContactModelV2) {
      // If is not internal contact, and has no phoneNumber, don't show any actions.
      if (contact.source !== 'Bhive' && !contact.phoneNumbers?.length) {
        return null;
      }

      // If is own user, don't show any actions.
      if (
        contact.source === 'Bhive' &&
        contact.sourceId === personStore.loggedInPersonId.toString()
      ) {
        return null;
      }
    }

    return (
      <div className="flex justify-center gap-6">
        {chatButton}
        {callButton}
        {videoButton}
      </div>
    );
  }
);
